import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Chip,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Checkbox,
  Pagination,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { regions, authCodes } from '../services/api';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PrintIcon from '@mui/icons-material/Print';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const ProductCodesPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [regionsList, setRegionsList] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [allProductCodes, setAllProductCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [sendToPrinting, setSendToPrinting] = useState(false);
  
  // Table and pagination state
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCodes, setTotalCodes] = useState(0);
  const [displayedCodes, setDisplayedCodes] = useState([]);
  
  // Download menu state
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  
  // Region management states
  const [addRegionDialog, setAddRegionDialog] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [newRegionCode, setNewRegionCode] = useState('');
  const [regionLoading, setRegionLoading] = useState(false);

  useEffect(() => {
    fetchRegions();
    // Don't call fetchAllProductCodes here, let the filter useEffect handle it
  }, []);

  // Set default date range (7 days ago to today)
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    // Format dates in local timezone
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setStartDate(formatDate(sevenDaysAgo));
    setEndDate(formatDate(today));
  }, []);

  // Initial load when dates are set
  useEffect(() => {
    if (startDate && endDate && allProductCodes.length === 0) {
      fetchAllProductCodes(1);
    }
  }, [startDate, endDate]);

  // Client-side pagination
  useEffect(() => {
    if (allProductCodes.length > 0) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedCodes = allProductCodes.slice(startIndex, endIndex);
      setDisplayedCodes(paginatedCodes);
      
      // Calculate total pages
      const totalPages = Math.ceil(allProductCodes.length / itemsPerPage);
      setTotalPages(totalPages);
    }
  }, [allProductCodes, currentPage, itemsPerPage]);


  const fetchRegions = async () => {
    try {
      const response = await regions.getAll();
      setRegionsList(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch regions');
      setLoading(false);
    }
  };

  const fetchAllProductCodes = async (page = 1) => {
    try {
      setCodesLoading(true);
      
      // Build query parameters - fetch all records for scrolling
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        regionId: regionFilter !== 'all' ? regionFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };
      
      console.log('Fetching product codes with params:', params);
      
      const response = await authCodes.getAll(params);
      const data = response.data || response;
      
      console.log('API response:', data);
      
      setAllProductCodes(data.codes || []);
      setTotalCodes(data.codes?.length || 0);
      setError('');
    } catch (err) {
      setError('Failed to fetch product codes');
      console.error('Error fetching product codes:', err);
    } finally {
      setCodesLoading(false);
    }
  };

  const handleAddRegion = async () => {
    if (!newRegionName || !newRegionCode) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setRegionLoading(true);
      const response = await regions.add({
        name: newRegionName,
        code: newRegionCode
      });
      
      setSuccess('Region added successfully!');
      setAddRegionDialog(false);
      setNewRegionName('');
      setNewRegionCode('');
      fetchRegions();
    } catch (err) {
      setError('Failed to add region');
    } finally {
      setRegionLoading(false);
    }
  };

  const handleGenerateCodes = async (e) => {
    e.preventDefault();
    if (!selectedRegion) {
      setError('Please select a region');
      return;
    }

    try {
      setLoading(true);
      const response = await authCodes.generate({
        regionId: selectedRegion,
        quantity: parseInt(quantity),
        sendToPrinting: sendToPrinting
      });
      setGeneratedCodes(response.data);
      setSuccess('Product codes generated successfully!');
      fetchAllProductCodes(); // Refresh the list
    } catch (err) {
      setError('Failed to generate product codes');
    } finally {
      setLoading(false);
    }
  };

  const downloadRegionQRsPDF = async () => {
    try {
      const pdf = new jsPDF();
      const pageHeight = pdf.internal.pageSize.height;
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      const startY = 30;
      
      for (let i = 0; i < regionsList.length; i++) {
        const region = regionsList[i];
        
        // Add new page for each region (except the first one)
        if (i > 0) {
          pdf.addPage();
        }
        
        // Reset yPosition for each page
        let yPosition = startY;
        
        // Add region name
        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.text(region.name, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;
        
        // Add QR code
        if (region.qrCode) {
          const qrCodeImg = new Image();
          qrCodeImg.crossOrigin = 'anonymous';
          qrCodeImg.src = region.qrCode;
          
          await new Promise((resolve, reject) => {
            qrCodeImg.onload = () => {
              try {
                // Center the QR code on the page
                const qrSize = 80; // Size of QR code
                const qrX = (pageWidth - qrSize) / 2;
                pdf.addImage(qrCodeImg, 'PNG', qrX, yPosition, qrSize, qrSize);
                yPosition += qrSize + 15;
                resolve();
              } catch (error) {
                reject(error);
              }
            };
            qrCodeImg.onerror = () => {
              reject(new Error('Failed to load QR code image'));
            };
          });
        }
        
        // Add region code
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Code: ${region.code}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;
      }
      
      pdf.save('regions-qr-codes.pdf');
    } catch (err) {
      console.error('PDF generation error:', err);
      setError('Failed to generate PDF: ' + (err.message || 'Unknown error'));
    }
  };

  const downloadSingleQR = async (region) => {
    try {
      const pdf = new jsPDF();
      
      // Add region name
      pdf.setFontSize(16);
      pdf.text(region.name, 20, 20);
      
      // Add QR code
      if (region.qrCode) {
        const qrCodeImg = new Image();
        qrCodeImg.src = region.qrCode;
        
        await new Promise((resolve) => {
          qrCodeImg.onload = () => {
            pdf.addImage(qrCodeImg, 'PNG', 20, 40, 100, 100);
            resolve();
          };
        });
        
        // Add region code
        pdf.setFontSize(12);
        pdf.text(`Code: ${region.code}`, 20, 150);
      }
      
      pdf.save(`${region.name}-qr-code.pdf`);
    } catch (err) {
      setError('Failed to generate PDF');
    }
  };

  // Handle checkbox selection
  const handleCodeSelection = (codeId, checked) => {
    // Find the code to check if it's used
    const code = allProductCodes.find(c => (c.id || c.productCode) === codeId);
    if (code && code.isUsed) {
      return; // Don't allow selection of used codes
    }
    
    if (checked) {
      setSelectedCodes(prev => [...prev, codeId]);
    } else {
      setSelectedCodes(prev => prev.filter(id => id !== codeId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Only select available (not used) codes from current page
      const availableCodes = displayedCodes.filter(code => !code.isUsed);
      const newSelections = availableCodes.map(code => code.id || code.productCode);
      setSelectedCodes(prev => [...new Set([...prev, ...newSelections])]);
    } else {
      // Remove only current page selections
      const currentPageCodes = displayedCodes.map(code => code.id || code.productCode);
      setSelectedCodes(prev => prev.filter(id => !currentPageCodes.includes(id)));
    }
  };

  // Filter available codes (not used) from current page only
  const availableCodes = displayedCodes.filter(code => !code.isUsed);

  // Download selected codes as PDF
  const handleDownloadSelected = async () => {
    if (selectedCodes.length === 0) {
      setError('Please select codes to download');
      return;
    }

    try {
      const pdf = new jsPDF();
      const selectedCodesData = allProductCodes.filter(code => 
        selectedCodes.includes(code.id || code.productCode) && !code.isUsed
      );
      
      for (let i = 0; i < selectedCodesData.length; i++) {
        const code = selectedCodesData[i];
        
        if (i > 0) pdf.addPage();
        
        // Add product code
        pdf.setFontSize(16);
        pdf.text(code.productCode, 20, 20);
        
        // Add QR code
        if (code.qrCode) {
          const qrCodeImg = new Image();
          qrCodeImg.src = code.qrCode;
          
          await new Promise((resolve) => {
            qrCodeImg.onload = () => {
              pdf.addImage(qrCodeImg, 'PNG', 20, 40, 100, 100);
              resolve();
            };
          });
          
          // Add authenticity code
          pdf.setFontSize(12);
          pdf.text(`Auth: ${code.authenticityCode}`, 20, 150);
        }
      }
      
      pdf.save('selected-product-codes.pdf');
    } catch (err) {
      setError('Failed to generate PDF');
    }
  };

  const handleDownloadSelectedCSV = () => {
    if (selectedCodes.length === 0) {
      setError('Please select codes to download');
      return;
    }

    try {
      const selectedCodesData = allProductCodes.filter(code => 
        selectedCodes.includes(code.id || code.productCode) && !code.isUsed
      );
      
      // Create CSV content
      const csvHeader = 'Region,Auth Code,Generated At\n';
      const csvRows = selectedCodesData.map(code => {
        const region = code.regionName || 'N/A';
        const authCode = code.authenticityCode || 'N/A';
        const generatedAt = code.createdAt ? new Date(code.createdAt).toLocaleString() : 'N/A';
        return `"${region}","${authCode}","${generatedAt}"`;
      }).join('\n');
      
      const csvContent = csvHeader + csvRows;
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'selected-product-codes.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('CSV file downloaded successfully');
    } catch (err) {
      setError('Failed to generate CSV');
    }
  };

  // Menu handlers
  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownloadAsPDF = () => {
    handleDownloadSelected();
    handleDownloadMenuClose();
  };

  const handleDownloadAsCSV = () => {
    handleDownloadSelectedCSV();
    handleDownloadMenuClose();
  };

  const handleSendToPrinting = async () => {
    if (selectedCodes.length === 0) {
      setError('Please select codes to send to printing');
      return;
    }

    try {
      // Update local state immediately for better UX
      const updatedCodes = allProductCodes.map(code => 
        selectedCodes.includes(code.id || code.productCode)
          ? { ...code, sendToPrinting: true }
          : code
      );

      // Get product codes for the selected items
      const productCodesToUpdate = selectedCodes.map(codeId => {
        const code = allProductCodes.find(c => (c.id || c.productCode) === codeId);
        return code ? code.productCode : null;
      }).filter(Boolean);

      // Use bulk update API
      await authCodes.updateManyPrintingStatus(productCodesToUpdate, true);
      
      setAllProductCodes(updatedCodes);
      setSuccess(`Sent ${selectedCodes.length} codes to printing`);
    } catch (error) {
      console.error('Error sending codes to printing:', error);
      setError('Failed to send codes to printing');
      
      // Revert local state on error
      const revertedCodes = allProductCodes.map(code => 
        selectedCodes.includes(code.id || code.productCode)
          ? { ...code, sendToPrinting: false }
          : code
      );
      setAllProductCodes(revertedCodes);
    }
  };

  const downloadProductCodesPDF = async () => {
    if (!generatedCodes) return;

    try {
      const pdf = new jsPDF();
      const codesPerPage = 4;
      let pageCount = 0;
      
      for (let i = 0; i < generatedCodes.codes.length; i++) {
        if (i % codesPerPage === 0 && i > 0) {
          pdf.addPage();
          pageCount++;
        }
        
        const code = generatedCodes.codes[i];
        const xPosition = (i % codesPerPage) % 2 === 0 ? 20 : 110;
        const yPosition = 20 + (Math.floor((i % codesPerPage) / 2) * 100);
        
        // Add QR code
        const qrCodeImg = new Image();
        qrCodeImg.src = code.qrCode;
        
        await new Promise((resolve) => {
          qrCodeImg.onload = () => {
            pdf.addImage(qrCodeImg, 'PNG', xPosition, yPosition, 60, 60);
            resolve();
          };
        });
        
        // Add product code below QR code
        pdf.setFontSize(10);
        pdf.text(code.productCode, xPosition, yPosition + 65);
        pdf.text(`Auth: ${code.authenticityCode}`, xPosition, yPosition + 75);
      }
      
      pdf.save(`product-codes-${generatedCodes.batch}.pdf`);
    } catch (err) {
      setError('Failed to generate PDF');
    }
  };

  const handleDownload = () => {
    if (!generatedCodes) return;

    // Create CSV content
    const csvContent = 'Region,Authenticity Code,Generated At\n' +
      generatedCodes.codes.map(code => 
        `${selectedRegion},${code.authenticityCode},${new Date(code.createdAt).toLocaleString()}`
      ).join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-codes-${generatedCodes.batch}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Product Authentication Codes
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Region Management" />
          <Tab label="Generate Product Codes" />
          <Tab label="All Product Codes" />
        </Tabs>
      </Paper>

      {/* Region Management Tab */}
      {tabValue === 0 && (
        <Box>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Regions</Typography>
              <Button
                sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddRegionDialog(true)}
              >
                Add Region
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Region Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regionsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography color="textSecondary">
                          No regions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    regionsList.map((region) => (
                      <TableRow key={region.id || region.code}>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {region.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {region.code}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {regionsList.length > 0 && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={downloadRegionQRsPDF}
                >
                  Download All Region QR Codes as PDF
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Generate Product Codes Tab */}
      {tabValue === 1 && (
        <Box>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Generate Product Codes
            </Typography>
            <Box component="form" onSubmit={handleGenerateCodes}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Region</InputLabel>
                    <Select
                      value={selectedRegion}
                      label="Region"
                      onChange={(e) => setSelectedRegion(e.target.value)}
                    >
                      {regionsList.map((region) => (
                        <MenuItem key={region.id || region.code} value={region.id || region.code}>
                          {region.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(1000, parseInt(e.target.value))))}
                    InputProps={{ inputProps: { min: 1, max: 1000 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendToPrinting}
                          onChange={(e) => setSendToPrinting(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Send to Printing"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      sx={{ ml: 'auto' , backgroundColor: "#c1ec4b", color: "black" }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Generate Product Codes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Generated Codes Section */}
          {generatedCodes && (
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                Successfully generated {generatedCodes.quantity} product codes.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    sx={{ color: "c1ec4b" }}
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownload}
                  >
                    Download CSV
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<QrCodeIcon />}
                    onClick={downloadProductCodesPDF}
                  >
                    Download PDF with QR Codes
                  </Button>
                </Box>
              </Box>
              
              {/* Display Generated Codes as Table */}
              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Region</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Auth Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Send to Printing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generatedCodes.codes.map((code, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2">
                            {code.region || selectedRegion || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {code.authenticityCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant={code.sendToPrinting ? "contained" : "outlined"}
                              color="success"
                              startIcon={<CheckIcon />}
                              onClick={async () => {
                                try {
                                  // Update local state immediately for better UX
                                  const updatedCodes = [...generatedCodes.codes];
                                  updatedCodes[index].sendToPrinting = true;
                                  setGeneratedCodes({ ...generatedCodes, codes: updatedCodes });
                                  
                                  // Update on server
                                  await authCodes.updatePrintingStatus(code.productCode, true);
                                  setSuccess(`Printing status updated for ${code.productCode}`);
                                } catch (error) {
                                  console.error('Error updating printing status:', error);
                                  setError('Failed to update printing status');
                                  
                                  // Revert local state on error
                                  const revertedCodes = [...generatedCodes.codes];
                                  revertedCodes[index].sendToPrinting = false;
                                  setGeneratedCodes({ ...generatedCodes, codes: revertedCodes });
                                }
                              }}
                              sx={{ minWidth: '60px' }}
                            >
                              Yes
                            </Button>
                            <Button
                              size="small"
                              variant={!code.sendToPrinting ? "contained" : "outlined"}
                              color="error"
                              startIcon={<CloseIcon />}
                              onClick={async () => {
                                try {
                                  // Update local state immediately for better UX
                                  const updatedCodes = [...generatedCodes.codes];
                                  updatedCodes[index].sendToPrinting = false;
                                  setGeneratedCodes({ ...generatedCodes, codes: updatedCodes });
                                  
                                  // Update on server
                                  await authCodes.updatePrintingStatus(code.productCode, false);
                                  setSuccess(`Printing status updated for ${code.authenticityCode}`);
                                } catch (error) {
                                  console.error('Error updating printing status:', error);
                                  setError('Failed to update printing status');
                                  
                                  // Revert local state on error
                                  const revertedCodes = [...generatedCodes.codes];
                                  revertedCodes[index].sendToPrinting = true;
                                  setGeneratedCodes({ ...generatedCodes, codes: revertedCodes });
                                }
                              }}
                              sx={{ minWidth: '60px' }}
                            >
                              No
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* All Product Codes Tab */}
      {tabValue === 2 && (
        <Box>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">All Generated Product Codes</Typography>
              <Button
                sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                variant="contained"
                onClick={fetchAllProductCodes}
                disabled={codesLoading}
              >
                {codesLoading ? <CircularProgress size={24} /> : 'Refresh'}
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="used">Used</MenuItem>
                      <MenuItem value="available">Available</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Region</InputLabel>
                    <Select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} label="Region">
                      <MenuItem value="all">All Regions</MenuItem>
                      {regionsList.map((region) => (
                        <MenuItem key={region.id} value={region.id}>
                          {region.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField 
                    fullWidth 
                    label="Items Per Page" 
                    type="number" 
                    value={itemsPerPage} 
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value && value > 0 && value <= totalCodes) {
                        setItemsPerPage(value);
                      } else if (value && value > totalCodes) {
                        setItemsPerPage(totalCodes);
                      } else if (value && value <= 0) {
                        setItemsPerPage(1);
                      }
                    }}
                    inputProps={{ 
                      min: 1, 
                      max: totalCodes || 1000,
                      step: 1 
                    }}
                    helperText={`Max: ${totalCodes || 'Loading...'}`}
                    InputLabelProps={{ shrink: true }} 
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setCurrentPage(1);
                        fetchAllProductCodes(1);
                      }} 
                      disabled={codesLoading} 
                      sx={{ flex: 1 }}
                    >
                      Apply Filters
                    </Button>
                    <Button 
                      variant="text" 
                      onClick={() => {
                        setStatusFilter('all');
                        setRegionFilter('all');
                        setItemsPerPage(20);
                        const today = new Date();
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(today.getDate() - 7);
                        const formatDate = (date) => {
                          return date.toISOString().split('T')[0];
                        };
                        setStartDate(formatDate(sevenDaysAgo));
                        setEndDate(formatDate(today));
                        setCurrentPage(1);
                        fetchAllProductCodes(1);
                      }} 
                      disabled={codesLoading}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Action Buttons Row */}
            {selectedCodes.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={handleDownloadMenuOpen}
                >
                  Download Selected ({selectedCodes.length})
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={handleSendToPrinting}
                >
                  Send To Printing ({selectedCodes.length})
                </Button>
                <Menu
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor)}
                  onClose={handleDownloadMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <MenuItem onClick={handleDownloadAsPDF}>
                    <ListItemIcon>
                      <PictureAsPdfIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>As PDF</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleDownloadAsCSV}>
                    <ListItemIcon>
                      <TableChartIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>As CSV</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {codesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer 
                sx={{ 
                  maxHeight: 600, 
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={availableCodes.length > 0 && availableCodes.every(code => selectedCodes.includes(code.id || code.productCode))}
                          indeterminate={availableCodes.some(code => selectedCodes.includes(code.id || code.productCode)) && !availableCodes.every(code => selectedCodes.includes(code.id || code.productCode))}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={availableCodes.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Region</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Auth Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Generated At</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Used At</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Download</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Send to Printing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedCodes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="textSecondary">
                            No product codes found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedCodes.map((code) => (
                        <TableRow key={code.id || code.productCode}>
                          <TableCell>
                            <Checkbox
                              checked={selectedCodes.includes(code.id || code.productCode)}
                              onChange={(e) => handleCodeSelection(code.id || code.productCode, e.target.checked)}
                              disabled={code.isUsed}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {code.regionName || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {code.authenticityCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={code.isUsed ? 'Used' : 'Available'} 
                              color={code.isUsed ? 'error' : 'success'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {code.createdAt ? new Date(code.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {code.usedAt ? new Date(code.usedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'Never'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => {
                                const pdf = new jsPDF();
                                pdf.setFontSize(16);
                                pdf.text(code.productCode, 20, 20);
                                if (code.qrCode) {
                                  const qrCodeImg = new Image();
                                  qrCodeImg.src = code.qrCode;
                                  qrCodeImg.onload = () => {
                                    pdf.addImage(qrCodeImg, 'PNG', 20, 40, 100, 100);
                                    pdf.text(`Auth: ${code.authenticityCode}`, 20, 150);
                                    pdf.save(`${code.productCode}.pdf`);
                                  };
                                } else {
                                  pdf.save(`${code.productCode}.pdf`);
                                }
                              }}
                            >
                              PDF
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant={code.sendToPrinting ? "contained" : "outlined"}
                                color="success"
                                startIcon={<CheckIcon />}
                                onClick={async () => {
                                  try {
                                    // Update local state immediately for better UX
                                    const updatedCodes = allProductCodes.map(c => 
                                      c.productCode === code.productCode 
                                        ? { ...c, sendToPrinting: true }
                                        : c
                                    );                                    
                                    // Update on server
                                    await authCodes.updatePrintingStatus(code.productCode, true);
                                    setAllProductCodes(updatedCodes);
                                    setSuccess(`Printing status updated for ${code.authenticityCode}`);
                                  } catch (error) {
                                    console.error('Error updating printing status:', error);
                                    setError('Failed to update printing status');
                                    
                                    // Revert local state on error
                                    const revertedCodes = allProductCodes.map(c => 
                                      c.productCode === code.productCode 
                                        ? { ...c, sendToPrinting: false }
                                        : c
                                    );
                                    setAllProductCodes(revertedCodes);
                                  }
                                }}
                                sx={{ minWidth: '60px' }}
                              >
                                Yes
                              </Button>
                              <Button
                                size="small"
                                variant={!code.sendToPrinting ? "contained" : "outlined"}
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={async () => {
                                  try {
                                    // Update local state immediately for better UX
                                    const updatedCodes = allProductCodes.map(c => 
                                      c.productCode === code.productCode 
                                        ? { ...c, sendToPrinting: false }
                                        : c
                                    );                                    
                                    // Update on server
                                    await authCodes.updatePrintingStatus(code.productCode, false);
                                    setAllProductCodes(updatedCodes);
                                    setSuccess(`Printing status updated for ${code.authenticityCode}`);
                                  } catch (error) {
                                    console.error('Error updating printing status:', error);
                                    setError('Failed to update printing status');
                                    
                                    // Revert local state on error
                                    const revertedCodes = allProductCodes.map(c => 
                                      c.productCode === code.productCode 
                                        ? { ...c, sendToPrinting: true }
                                        : c
                                    );
                                    setAllProductCodes(revertedCodes);
                                  }
                                }}
                                sx={{ minWidth: '60px' }}
                              >
                                No
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => {
                    setCurrentPage(page);
                  }}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {/* Summary */}
            {allProductCodes.length > 0 && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Showing {displayedCodes.length} of {allProductCodes.length} product codes (Page {currentPage} of {totalPages}, {itemsPerPage} per page)
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Add Region Dialog */}
      <Dialog open={addRegionDialog} onClose={() => setAddRegionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Region</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Region Name"
              value={newRegionName}
              onChange={(e) => setNewRegionName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Region Code"
              value={newRegionCode}
              onChange={(e) => setNewRegionCode(e.target.value.toUpperCase())}
              margin="normal"
              helperText="This will be used to generate QR codes"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRegionDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddRegion} 
            variant="contained" 
            disabled={regionLoading}
          >
            {regionLoading ? <CircularProgress size={24} /> : 'Add Region'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductCodesPage;